'use client';

import { reduce, map as _map } from 'lodash-es';
import { Fragment, useMemo } from 'react';
import moment from 'moment';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { makeStyles } from 'tss-react/mui';
import EventList from './event-list';
import { momentParse } from '../utils/time';
import type { Event } from '../types/Event';
import type { Group } from '../types/Group/Group';

const useStyles = makeStyles()(() => ({
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    '&:first-of-type': {
      marginTop: 0,
    },
  },
}));

type Props = {
  events?: Event[];
  allowPast?: boolean;
  showPastDates?: boolean;
  timezone?: string;
  group?: Group;
  fullDate?: boolean;
  disableContainer?: boolean;
  emptyMessage?: string;
};

const DatedEventList = (props: Props) => {
  const { events, allowPast = false, showPastDates = false, timezone, group, fullDate, disableContainer = false, emptyMessage } = props;
  const { classes } = useStyles();

  // Compute "now" in the city's timezone so friendly date labels ("Today",
  // "Tomorrow", etc.) render correctly on both server and client.
  const now = useMemo(() => momentParse(undefined, timezone), [timezone]);

  const groupedEvents = useMemo(() => {
    if (!events || !events.length) {
      return null;
    }

    return reduce(events, (acc: Record<string, { value: number; dayOfWeek: string; date: string; events: Event[] }>, event) => {
      const { startAt: origStartAt, timezone } = event;

      let value;
      let dayOfWeek;
      let date;

      if (!origStartAt) {
        value = 0;
        dayOfWeek = 'No Date';
        date = '';
      } else {
        let startAt = momentParse(origStartAt, timezone);
        if (!allowPast && !showPastDates && startAt.isBefore(now)) {
          startAt = now;
        }

        value = moment(startAt).startOf('day').valueOf();
        dayOfWeek = startAt.format('dddd');
        date = startAt.format('MMMM D, YYYY');
      }

      acc[value] = acc[value] || { value, dayOfWeek, date, events: [] };
      acc[value].events.push(event);
      return acc;
    }, {});
  }, [events, allowPast, showPastDates, now]);

  const Wrapper = disableContainer ? 'div' : Container;
  const wrapperProps = disableContainer ? {} : { maxWidth: 'md' as const, disableGutters: true };

  if (!events || !events.length) {
    return (
      <Wrapper {...wrapperProps}>
        {!disableContainer && <Typography variant="h2">{allowPast ? 'Past' : 'Upcoming'} Events</Typography>}
        <p>{emptyMessage ?? `No ${allowPast ? 'past' : 'upcoming'} events.`}</p>
      </Wrapper>
    );
  }

  return (
    <Wrapper {...wrapperProps}>
      {_map(groupedEvents, ({ dayOfWeek, date, events }) => {
        if (!events) {
          return null;
        }

        let displayDayOfWeek = dayOfWeek;
        const startAt = momentParse(events[0].startAt, events[0].timezone);
        if (!startAt.isBefore(now, 'day')) {
          if (startAt.isSame(now, 'day')) {
            displayDayOfWeek = 'Today';
          } else if (startAt.isSame(moment(now).add(1, 'day'), 'day')) {
            displayDayOfWeek = 'Tomorrow';
          } else if (startAt.isSame(now, 'week')) {
            displayDayOfWeek = `This ${dayOfWeek}`;
          } else if (startAt.isSame(moment(now).add(1, 'week'), 'week')) {
            displayDayOfWeek = `Next ${dayOfWeek}`;
          }
        }

        return (
          <Fragment key={dayOfWeek + date}>
            <Typography variant="h2" className={classes.groupHeader}>
              <span>
                {displayDayOfWeek}
                {date && (
                  <span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>
                    {date}
                  </span>
                )}
              </span>
            </Typography>

            <EventList events={events} group={group} fullDate={fullDate} />
          </Fragment>
        );
      })}
    </Wrapper>
  )
};

export default DatedEventList;
